import {
  ModalCloseButton,
  createStackHeaderNavigator,
} from "@towersystems/roam-common-native";
import {
  EditInvoiceArrivalRootParamList,
  InvoiceArrivalRootParamList,
} from "../../../../types";
import {
  CardStyleInterpolators,
  StackScreenProps,
} from "@react-navigation/stack";
import { EditInvoiceArrivalDetailsScreen } from "../EditInvoiceArrivalDetailsScreen";
import { useLazyQuery } from "@apollo/client";
import { QUERY_INVOICE_ARRIVAL } from "../../utils";
import React from "react";
import { InvoiceArrival } from "@towersystems/roam-common/lib/generated-types";
import {
  ActiveInvoiceArrivalContext,
  useInvoiceArrival,
} from "../../utils/active-invoice-arrival";
import { AddProductModalRootScreen } from "../AddProductModalRootScreen";
import { GenericEditFormInputScreen } from "../GenericEditFormInputScreen";
import { EditInvoiceLineItemRootScreen } from "../EditInvoiceLineItemRootScreen";
import { PageErrorOccurred } from "../../../../components";
import { PlaceholderLoadingScreen } from "../../../products/screens/PlaceholderLoadingScreen";
import { useHasTransitionEnded } from "../../../../utilities/use-after-interactions";
import { ResourcePageSkeleton } from "../../../point-of-sale/screens/ResourcPageSkeleton";

export type EditInvoiceArrivalRootScreenProps = StackScreenProps<
  InvoiceArrivalRootParamList,
  "EditInvoiceArrivalRootScreen"
>;

const EditInvoiceArrivalRoot = ({
  invoiceArrival,
}: EditInvoiceArrivalRootScreenProps & {
  invoiceArrival: InvoiceArrival;
}) => {
  const Drawer = createStackHeaderNavigator<EditInvoiceArrivalRootParamList>();
  const context = useInvoiceArrival(invoiceArrival);

  return (
    <ActiveInvoiceArrivalContext.Provider value={context}>
      <Drawer.Navigator
        screenOptions={{
          animationEnabled: true,
          headerLeft: () => <ModalCloseButton type="Back" />,
          title: "",
        }}
      >
        <Drawer.Group>
          <Drawer.Screen
            name="EditInvoiceArrivalDetailsScreen"
            component={EditInvoiceArrivalDetailsScreen}
          />
        </Drawer.Group>
        <Drawer.Group
          screenOptions={{
            headerShown: false,
            presentation: "modal",
          }}
        >
          <Drawer.Screen
            name="AddProductModalRootScreen"
            component={AddProductModalRootScreen}
          />
        </Drawer.Group>

        <Drawer.Group
          screenOptions={{
            headerShown: false,
            cardStyleInterpolator: CardStyleInterpolators.forHorizontalIOS,
          }}
        >
          <Drawer.Screen
            name="EditInvoiceLineItemRootScreen"
            component={EditInvoiceLineItemRootScreen}
          />
          <Drawer.Screen
            name="GenericEditFormInputScreen"
            component={GenericEditFormInputScreen}
            options={{
              headerShown: true,
              headerLeft: () => <ModalCloseButton type="Cancel" />,
            }}
          />
        </Drawer.Group>
      </Drawer.Navigator>
    </ActiveInvoiceArrivalContext.Provider>
  );
};

export const EditInvoiceArrivalRootScreen = ({
  navigation,
  route,
}: EditInvoiceArrivalRootScreenProps) => {
  const [invoiceArrival, setInvoiceArrival] = React.useState<
    InvoiceArrival | undefined
  >(undefined);

  const [error, setError] = React.useState<string | undefined>(undefined);
  const transitionEnded = useHasTransitionEnded(navigation);

  const id = route.params?.invoiceArrivalId;
  const [q] = useLazyQuery(QUERY_INVOICE_ARRIVAL);

  React.useEffect(() => {
    if (id) {
      q({
        variables: {
          id,
        },
      })
        .then((r) => {
          if (!r.data || r.error) {
            setError("Not found");
          }
          setInvoiceArrival(r.data?.invoiceArrival);
        })
        .catch((e) => {
          setError("Not found");
        });
    }
  }, []);

  if (!invoiceArrival && error) {
    return (
      <PageErrorOccurred
        errorMessage={error}
        onRetry={() => {
          navigation.reset({
            index: 0,
            routes: [{ name: "InvoiceArrivalsLandingScreen" }],
          });
        }}
      />
    );
  }

  // Wait for transition to end before rendering the screen
  // otherwise the screen animation will be janky
  if (!invoiceArrival || false == transitionEnded) {
    return (
      <PlaceholderLoadingScreen
        navigation={navigation}
        content={ResourcePageSkeleton}
      />
    );
  }

  return (
    <EditInvoiceArrivalRoot
      navigation={navigation}
      route={route}
      invoiceArrival={invoiceArrival}
    />
  );
};
